import {css, StyleSheet} from 'aphrodite';
import classnames from 'classnames';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {ACTION_CATEGORY} from 'constants/AnalyticsConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {
  BLUE_LIGHT,
  BLUE_LIGHTER,
  BLUE_POWDER,
  BLUE_POWDER_LIGHT,
  GRAY,
  GRAY_LIGHTER,
} from 'ui-library/Colors';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

export enum ButtonType {
  FROM_SCRATCH,
  FROM_DRAFT,
  FROM_MANIFEST,
}

interface BenchlingAppCreateChoiceButtonProps {
  type: ButtonType;
  isSelected: boolean;
  handleClick: (value: ButtonType) => void;
  dataTestAttrs?: DataTestAttrs;
}

export default function CreateChoiceButton(props: BenchlingAppCreateChoiceButtonProps): JSX.Element {
  function handleClick(): void {
    analytics.trackEventWithModule(
      {
        module: ANALYTICS_MODULE.BENCHLING_APPS,
        action: 'clicked_create_app_choice',
        actionCategory: ACTION_CATEGORY.READ,
      },
      ANALYTICS_MODULE.BENCHLING_APPS,
      {buttonType: props.type}
    );
    props.handleClick(props.type);
  }

  const bodyText: Record<ButtonType, string> = {
    [ButtonType.FROM_SCRATCH]: 'From scratch',
    [ButtonType.FROM_DRAFT]: 'From existing',
    [ButtonType.FROM_MANIFEST]: 'From manifest',
  };

  const iconNames: Record<ButtonType, BenchlingIconName> = {
    [ButtonType.FROM_SCRATCH]: 'create',
    [ButtonType.FROM_DRAFT]: 'create',
    [ButtonType.FROM_MANIFEST]: 'code',
  };

  function getTargetCss(): string {
    switch (props.type) {
      case ButtonType.FROM_SCRATCH:
      case ButtonType.FROM_DRAFT: {
        return css(styles.targetBoxScratch);
      }
      case ButtonType.FROM_MANIFEST: {
        return css(styles.targetBoxManifest);
      }
      default:
        return props.type satisfies never;
    }
  }

  return (
    <div
      onClick={handleClick}
      className={css(
        styles.outerBox,
        props.isSelected ? styles.hoverSelected : styles.hover,
        props.isSelected ? styles.selected : null
      )}
      {...getTestProps(props.dataTestAttrs)}
    >
      <div
        className={classnames(
          getTargetCss(),
          css(styles.targetBox, props.isSelected ? styles.targetBoxSelected : null)
        )}
      >
        <BenchlingIcon icon={iconNames[props.type]} className={css(styles.icon)} />
        {props.type === ButtonType.FROM_MANIFEST ? (
          <BenchlingIcon icon="alignment" className={css(styles.manifestAlignIcon)} />
        ) : null}
      </div>
      <div className={css(styles.bodyText)}>{bodyText[props.type]}</div>
    </div>
  );
}

const styles = StyleSheet.create({
  outerBox: {
    padding: '2em',
    border: 'solid',
    borderWidth: 1.5,
    borderColor: GRAY,
    borderRadius: 4,
  },
  targetBox: {
    height: '6em',
    width: '5em',
    padding: '2em 0',
    marginRight: '2em',
    display: 'inline-block',
    borderWidth: 1.5,
    borderColor: GRAY,
    borderRadius: 4,
    color: GRAY,
    textAlign: 'center',
  },
  targetBoxManifest: {
    borderStyle: 'solid',
  },
  targetBoxScratch: {
    borderStyle: 'dashed',
  },
  selected: {
    color: BLUE_LIGHTER,
    borderColor: BLUE_LIGHT,
    backgroundColor: BLUE_POWDER_LIGHT,
  },
  targetBoxSelected: {
    color: BLUE_LIGHTER,
    borderColor: BLUE_LIGHTER,
  },
  hover: {
    ':hover': {
      backgroundColor: GRAY_LIGHTER,
    },
  },
  hoverSelected: {
    ':hover': {
      backgroundColor: BLUE_POWDER,
    },
  },
  bodyText: {
    position: 'relative',
    top: '-1em',
    display: 'inline-block',
  },
  icon: {
    fontSize: 16,
    margin: '0 auto',
    position: 'relative',
  },
  manifestAlignIcon: {
    fontSize: 16,
    position: 'absolute',
    bottom: '4em',
    left: '2.7em',
  },
});
